/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState, useMemo } from "react";
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    flexRender,
} from "@tanstack/react-table";
import {
    Box,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    InputGroup,
    InputLeftElement,
    Input,
    Select,
    Button,
    useToast,
    Badge,
    Heading,
    Text,
} from "@chakra-ui/react";
import { SearchIcon as ChakraSearchIcon } from "@chakra-ui/icons";
import { APIContext } from "../../api";
import { Link, useNavigate } from "react-router-dom";
import profilePhoto from "../../assets/profile_photo.svg";
import HelmetLayout from "../../components/HelmetLayout";
import { PulseLoader } from "react-spinners";
import DateDisplay from "../../components/ui-kit/date-display";

function Apps() {
    const [appsList, setAppsList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [byApp, setByApp] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [actioningId, setActioningId] = useState(null);
    const [unapprovingId, setUnapprovingId] = useState(null);
    const { getAppsList, getPendingAppReviews, approveRejectAppReview, unapproveApp } = useContext(APIContext);
    const toast = useToast();
    const [statusFilter, setStatusFilter] = useState("");
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState([{ id: "created_at", desc: true }]);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
    const navigate = useNavigate();

    const fetchApps = () => {
        setIsLoading(true);
        getAppsList(statusFilter)
            .then((apps) => setAppsList(apps))
            .catch((err) => console.error("Error fetching apps:", err))
            .finally(() => setIsLoading(false));
    };

    const fetchReviews = () => {
        setReviewsLoading(true);
        getPendingAppReviews()
            .then((list) => setByApp(Array.isArray(list) ? list : []))
            .catch((err) => {
                console.error("Error fetching pending app reviews:", err);
                toast({ title: "Error", description: "Could not load app reviews", status: "error", duration: 3000 });
            })
            .finally(() => setReviewsLoading(false));
    };

    useEffect(() => {
        fetchApps();
    }, [statusFilter]);

    const handleApproveReject = (reviewId, status) => {
        setActioningId(reviewId);
        approveRejectAppReview(reviewId, status)
            .then(() => {
                toast({
                    title: status === "approved" ? "Review approved" : "Review rejected",
                    status: "success",
                    duration: 3000,
                });
                fetchReviews();
            })
            .catch((err) => {
                toast({
                    title: "Error",
                    description: err?.response?.data?.error || "Failed to update review",
                    status: "error",
                    duration: 3000,
                });
            })
            .finally(() => setActioningId(null));
    };

    const handleUnapprove = (e, app) => {
        e.stopPropagation();
        if (!window.confirm(`Remove "${app.app_name}" from the Grigora app store? The app will move to development and the developer will be notified by email.`)) return;
        setUnapprovingId(app.id);
        unapproveApp(app.id)
            .then(() => {
                toast({
                    title: "App unapproved",
                    description: "App has been removed from the app store and the developer has been notified.",
                    status: "success",
                    duration: 4000,
                });
                fetchApps();
            })
            .catch((err) => {
                toast({
                    title: "Error",
                    description: err?.response?.data?.error || "Failed to unapprove app",
                    status: "error",
                    duration: 3000,
                });
            })
            .finally(() => setUnapprovingId(null));
    };

    const columns = useMemo(
        () => [
            {
                id: "app_name",
                header: "App Name",
                accessorKey: "app_name",
                cell: (info) => {
                    const app = info.row.original;
                    return (
                        <div className="flex items-center gap-3">
                            {app.icon_url ? (
                                <img src={app.icon_url} className="w-9 h-9 rounded-lg object-cover shadow-sm" alt="icon" />
                            ) : (
                                <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center text-xs text-gray-500 font-medium">
                                    App
                                </div>
                            )}
                            <span className="font-semibold text-gray-800">{app.app_name}</span>
                        </div>
                    );
                },
            },
            {
                id: "developer",
                header: "Developer",
                accessorKey: "developer_name",
                cell: (info) => <span className="text-gray-600">{info.getValue() || "—"}</span>,
            },
            {
                id: "status",
                header: "Status",
                accessorKey: "status",
                cell: (info) => {
                    const status = info.getValue();
                    const reviewStatus = info.row.original.review_status;
                    let colorScheme = "gray";
                    if (status === "production") colorScheme = "green";
                    if (reviewStatus === "pending") colorScheme = "yellow";
                    if (reviewStatus === "rejected") colorScheme = "red";
                    const label = reviewStatus ? reviewStatus : (status === "production" ? "Approved" : status);
                    return (
                        <Badge colorScheme={colorScheme} variant="subtle" px={2} py={1} borderRadius="md" textTransform="capitalize">
                            {String(label).replace(/_/g, " ")}
                        </Badge>
                    );
                },
            },
            {
                id: "featured",
                header: "Featured",
                cell: (info) => {
                    const app = info.row.original;
                    const featured = app.featured_app === true;
                    const idx = app.featured_index;
                    if (!featured) return <span className="text-gray-400">—</span>;
                    return (
                        <Badge colorScheme="yellow" variant="subtle" px={2} py={1} borderRadius="md">
                            {idx != null && idx !== "" ? `#${idx}` : "Yes"}
                        </Badge>
                    );
                },
            },
            {
                id: "created_at",
                header: "Created",
                accessorKey: "created_at",
                cell: (info) => {
                    const date = info.getValue() ? new Date(info.getValue()) : null;
                    return date ? <DateDisplay date={date} /> : "—";
                },
            },
            {
                id: "actions",
                header: "Actions",
                cell: (info) => {
                    const app = info.row.original;
                    const isApproved = app.status === "production" || app.review_status === "approved";
                    if (!isApproved) return <span className="text-gray-400">—</span>;
                    return (
                        <Button
                            size="sm"
                            colorScheme="orange"
                            variant="outline"
                            isLoading={unapprovingId === app.id}
                            onClick={(e) => handleUnapprove(e, app)}
                        >
                            Unapprove
                        </Button>
                    );
                },
            },
        ],
        [unapprovingId]
    );

    const table = useReactTable({
        data: appsList,
        columns,
        state: { sorting, pagination, globalFilter },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const filteredTotal = table.getPrePaginationRowModel().rows.length;
    const pendingCount = appsList.filter((a) => a.review_status === "pending").length;
    const totalPendingReviews = byApp.reduce((acc, app) => acc + (app.reviews?.length || 0), 0);

    return (
        <>
            <HelmetLayout title="App Management" />
            <Box className="font-Inter flex-1 min-h-screen overflow-y-auto" bg="gray.50">
                <Box maxW="7xl" mx="auto" px={{ base: 4, md: 8 }} py={8}>
                    <Box flexDirection={{ base: "column", md: "row" }} justifyContent="space-between" alignItems="center" mb={8} display="flex">
                        <Heading size="lg" fontWeight="700" color="gray.800">
                            App Management
                        </Heading>
                        <Box display={{ base: "none", md: "block" }}>
                            <img src={profilePhoto} alt="profile" height="36px" />
                        </Box>
                    </Box>

                    <Tabs
                        variant="enclosed-colored"
                        colorScheme="blue"
                        size="md"
                        isLazy
                        onChange={(index) => {
                            if (index === 1) fetchReviews();
                        }}
                    >
                        <TabList
                            bg="white"
                            borderRadius="xl"
                            p={1}
                            boxShadow="sm"
                            border="1px solid"
                            borderColor="gray.200"
                            mb={6}
                            gap={1}
                        >
                            <Tab
                                _selected={{ bg: "blue.500", color: "white", borderRadius: "lg", fontWeight: "600" }}
                                borderRadius="lg"
                                py={3}
                                px={6}
                            >
                                Apps
                            </Tab>
                            <Tab
                                _selected={{ bg: "blue.500", color: "white", borderRadius: "lg", fontWeight: "600" }}
                                borderRadius="lg"
                                py={3}
                                px={6}
                            >
                                App Reviews
                                {totalPendingReviews > 0 && (
                                    <Badge ml={2} colorScheme="orange" fontSize="xs">
                                        {totalPendingReviews}
                                    </Badge>
                                )}
                            </Tab>
                        </TabList>

                        <TabPanels>
                            {/* ——— Apps section ——— */}
                            <TabPanel px={0}>
                                <Box mb={6} display="flex" flexWrap="wrap" gap={4}>
                                    <Box
                                        bg="white"
                                        borderRadius="xl"
                                        p={6}
                                        minW="200px"
                                        boxShadow="sm"
                                        borderWidth="1px"
                                        borderColor="gray.100"
                                    >
                                        <Text fontSize="2xl" fontWeight="700" color="gray.800">
                                            {appsList.length}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500" fontWeight="500">
                                            Total Apps
                                        </Text>
                                    </Box>
                                    <Box
                                        bg="white"
                                        borderRadius="xl"
                                        p={6}
                                        minW="200px"
                                        boxShadow="sm"
                                        borderWidth="1px"
                                        borderColor="gray.100"
                                    >
                                        <Text fontSize="2xl" fontWeight="700" color="orange.500">
                                            {pendingCount}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500" fontWeight="500">
                                            Pending Review
                                        </Text>
                                    </Box>
                                </Box>

                                <Box bg="white" borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" overflow="hidden">
                                    <Box p={6} borderBottomWidth="1px" borderColor="gray.100">
                                        <Box display="flex" flexWrap="wrap" alignItems="center" gap={4}>
                                            <Select
                                                placeholder="All statuses"
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                size="md"
                                                maxW="200px"
                                                borderRadius="lg"
                                            >
                                                <option value="pending">Pending Review</option>
                                                <option value="approved">Approved</option>
                                                <option value="rejected">Rejected</option>
                                                <option value="development">In Development</option>
                                            </Select>
                                            <InputGroup maxW="320px" size="md">
                                                <InputLeftElement pointerEvents="none">
                                                    <ChakraSearchIcon color="gray.400" />
                                                </InputLeftElement>
                                                <Input
                                                    placeholder="Search apps..."
                                                    value={globalFilter ?? ""}
                                                    onChange={(e) => setGlobalFilter(e.target.value)}
                                                    borderRadius="lg"
                                                    bg="gray.50"
                                                />
                                            </InputGroup>
                                        </Box>
                                    </Box>

                                    <Box overflowX="auto">
                                        <Box as="table" minW="full" fontSize="sm">
                                            <Box as="thead" bg="gray.50">
                                                {table.getHeaderGroups().map((headerGroup) => (
                                                    <Box as="tr" key={headerGroup.id}>
                                                        {headerGroup.headers.map((header) => (
                                                            <Box
                                                                as="th"
                                                                key={header.id}
                                                                px={6}
                                                                py={4}
                                                                textAlign="left"
                                                                fontWeight="600"
                                                                color="gray.600"
                                                                cursor="pointer"
                                                                onClick={header.column.getToggleSortingHandler()}
                                                            >
                                                                <Box display="inline-flex" alignItems="center" gap={2}>
                                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                                    <span style={{ fontSize: "10px", color: "#a0aec0" }}>
                                                                        {header.column.getIsSorted() === "asc" ? "▲" : header.column.getIsSorted() === "desc" ? "▼" : ""}
                                                                    </span>
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                ))}
                                            </Box>
                                            <Box as="tbody">
                                                {isLoading ? (
                                                    <Box as="tr">
                                                        <Box as="td" colSpan={columns.length} py={12} textAlign="center">
                                                            <PulseLoader size={10} color="#3182CE" />
                                                        </Box>
                                                    </Box>
                                                ) : table.getRowModel().rows.length === 0 ? (
                                                    <Box as="tr">
                                                        <Box as="td" colSpan={columns.length} py={12} textAlign="center" color="gray.500">
                                                            No apps found
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    table.getRowModel().rows.map((row) => (
                                                        <Box
                                                            as="tr"
                                                            key={row.id}
                                                            _hover={{ bg: "gray.50" }}
                                                            cursor="pointer"
                                                            onClick={() => navigate(`/apps/${row.original.id}`)}
                                                            borderBottomWidth="1px"
                                                            borderColor="gray.50"
                                                        >
                                                            {row.getVisibleCells().map((cell) => (
                                                                <Box as="td" key={cell.id} px={6} py={4} whiteSpace="nowrap">
                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    ))
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        px={6}
                                        py={4}
                                        bg="gray.50"
                                        borderTopWidth="1px"
                                        borderColor="gray.100"
                                    >
                                        <Text fontSize="sm" color="gray.500">
                                            Showing {pagination.pageIndex * pagination.pageSize + 1}–{Math.min((pagination.pageIndex + 1) * pagination.pageSize, filteredTotal)} of {filteredTotal}
                                        </Text>
                                        <Box display="flex" alignItems="center" gap={3}>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => table.previousPage()}
                                                isDisabled={!table.getCanPreviousPage()}
                                            >
                                                Previous
                                            </Button>
                                            <Text fontSize="sm" color="gray.600">
                                                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() || 1}
                                            </Text>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => table.nextPage()}
                                                isDisabled={!table.getCanNextPage()}
                                            >
                                                Next
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            </TabPanel>

                            {/* ——— App Reviews section ——— */}
                            <TabPanel px={0}>
                                <Box mb={6} display="flex" flexWrap="wrap" gap={4}>
                                    <Box
                                        bg="white"
                                        borderRadius="xl"
                                        p={6}
                                        minW="200px"
                                        boxShadow="sm"
                                        borderWidth="1px"
                                        borderColor="gray.100"
                                    >
                                        <Text fontSize="2xl" fontWeight="700" color="orange.500">
                                            {totalPendingReviews}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500" fontWeight="500">
                                            Pending reviews
                                        </Text>
                                    </Box>
                                    <Box
                                        bg="white"
                                        borderRadius="xl"
                                        p={6}
                                        minW="200px"
                                        boxShadow="sm"
                                        borderWidth="1px"
                                        borderColor="gray.100"
                                    >
                                        <Text fontSize="2xl" fontWeight="700" color="gray.800">
                                            {byApp.length}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500" fontWeight="500">
                                            Apps with pending reviews
                                        </Text>
                                    </Box>
                                </Box>

                                {reviewsLoading ? (
                                    <Box bg="white" borderRadius="xl" p={12} textAlign="center" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                                        <PulseLoader color="#3182CE" size={12} />
                                    </Box>
                                ) : byApp.length === 0 ? (
                                    <Box bg="white" borderRadius="xl" p={10} textAlign="center" color="gray.500" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                                        No pending app reviews.
                                    </Box>
                                ) : (
                                    <Box display="flex" flexDirection="column" gap={6}>
                                        {byApp.map((app) => (
                                            <Box
                                                key={app.app_id}
                                                bg="white"
                                                borderRadius="xl"
                                                boxShadow="sm"
                                                borderWidth="1px"
                                                borderColor="gray.100"
                                                overflow="hidden"
                                            >
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={4}
                                                    p={6}
                                                    borderBottomWidth="1px"
                                                    borderColor="gray.100"
                                                    flexWrap="wrap"
                                                >
                                                    {app.icon_url ? (
                                                        <img src={app.icon_url} alt="" className="w-12 h-12 rounded-xl object-cover shadow-sm" />
                                                    ) : (
                                                        <Box w={12} h={12} bg="gray.100" borderRadius="xl" display="flex" alignItems="center" justifyContent="center" fontSize="xs" color="gray.500">
                                                            App
                                                        </Box>
                                                    )}
                                                    <Box flex={1} minW={0}>
                                                        <Text fontWeight="600" color="gray.800">
                                                            {app.app_name}
                                                        </Text>
                                                        <Text fontSize="sm" color="gray.500">
                                                            ID: {app.app_id}
                                                        </Text>
                                                    </Box>
                                                    <Button as={Link} to={`/apps/${app.app_id}`} size="sm" variant="outline" colorScheme="blue">
                                                        View app
                                                    </Button>
                                                </Box>
                                                <Box overflowX="auto">
                                                    <Box as="table" minW="full" fontSize="sm">
                                                        <Box as="thead" bg="gray.50">
                                                            <Box as="tr">
                                                                <Box as="th" px={6} py={3} textAlign="left" fontWeight="600" color="gray.600">Reviewer</Box>
                                                                <Box as="th" px={6} py={3} textAlign="left" fontWeight="600" color="gray.600">Rating</Box>
                                                                <Box as="th" px={6} py={3} textAlign="left" fontWeight="600" color="gray.600">Comment</Box>
                                                                <Box as="th" px={6} py={3} textAlign="left" fontWeight="600" color="gray.600">Date</Box>
                                                                <Box as="th" px={6} py={3} textAlign="right" fontWeight="600" color="gray.600">Actions</Box>
                                                            </Box>
                                                        </Box>
                                                        <Box as="tbody">
                                                            {(app.reviews || []).map((review) => (
                                                                <Box as="tr" key={review.id} borderTopWidth="1px" borderColor="gray.50">
                                                                    <Box as="td" px={6} py={4}>
                                                                        <Box display="flex" alignItems="center" gap={3}>
                                                                            {review.reviewer_photo_url ? (
                                                                                <img src={review.reviewer_photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                                                                            ) : (
                                                                                <Box w={9} h={9} borderRadius="full" bg="gray.200" display="flex" alignItems="center" justifyContent="center" fontSize="sm" fontWeight="600" color="gray.600">
                                                                                    {(review.reviewer_name || review.reviewer_username || "?")[0].toUpperCase()}
                                                                                </Box>
                                                                            )}
                                                                            <Box>
                                                                                <Text fontWeight="500" color="gray.800" fontSize="sm">
                                                                                    {review.reviewer_name || review.reviewer_username || "—"}
                                                                                </Text>
                                                                                {review.reviewer_email && (
                                                                                    <Text fontSize="xs" color="gray.500">{review.reviewer_email}</Text>
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                    <Box as="td" px={6} py={4} color="gray.600">
                                                                        {review.rating != null ? `${review.rating}/5` : "—"}
                                                                    </Box>
                                                                    <Box as="td" px={6} py={4} maxW="xs" color="gray.600" title={review.comment || ""}>
                                                                        <Text noOfLines={2}>{review.comment || "—"}</Text>
                                                                    </Box>
                                                                    <Box as="td" px={6} py={4} color="gray.500">
                                                                        {review.created_at ? <DateDisplay date={new Date(review.created_at)} /> : "—"}
                                                                    </Box>
                                                                    <Box as="td" px={6} py={4} textAlign="right">
                                                                        <Box display="inline-flex" gap={2}>
                                                                            <Button
                                                                                size="sm"
                                                                                colorScheme="green"
                                                                                isLoading={actioningId === review.id}
                                                                                onClick={() => handleApproveReject(review.id, "approved")}
                                                                            >
                                                                                Approve
                                                                            </Button>
                                                                            <Button
                                                                                size="sm"
                                                                                colorScheme="red"
                                                                                variant="outline"
                                                                                isLoading={actioningId === review.id}
                                                                                onClick={() => handleApproveReject(review.id, "rejected")}
                                                                            >
                                                                                Reject
                                                                            </Button>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </Box>
            </Box>
        </>
    );
}

export default Apps;
